import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, Check, AlertCircle, Loader2, ArrowRight } from 'lucide-react';

const API = ((typeof window !== "undefined" && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL) + '/api';

const PageShell = ({ children, title }) => (
  <div className="min-h-screen bg-zinc-950 text-zinc-100 flex flex-col">
    <header className="border-b border-zinc-900">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-zinc-950" />
          </div>
          <span className="font-bold text-lg">Home <span className="text-amber-400">MDB 2A</span></span>
        </Link>
      </div>
    </header>
    <div className="flex-1 flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <h1 className="text-3xl font-bold mb-6 text-center">{title}</h1>
        {children}
      </div>
    </div>
  </div>
);

export const VerifyEmailPage = () => {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [status, setStatus] = useState('loading'); // loading | ok | error
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setError('Lien invalide (token manquant)');
      return;
    }
    axios.post(`${API}/auth/verify-email`, { token })
      .then(() => setStatus('ok'))
      .catch((err) => {
        setStatus('error');
        setError(err.response?.data?.detail || 'Lien invalide ou expiré');
      });
  }, [token]);

  return (
    <PageShell title={status === 'loading' ? 'Vérification...' : (status === 'ok' ? 'Email vérifié 🎉' : 'Erreur')}>
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-8 text-center">
        {status === 'loading' && <Loader2 className="w-12 h-12 text-amber-400 animate-spin mx-auto" />}
        {status === 'ok' && (
          <>
            <Check className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
            <p className="text-zinc-300 mb-6">Ton compte est activé. Tu peux te connecter et profiter de tes 14 jours d'essai.</p>
            <Link to="/app" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold transition-colors">
              Aller au dashboard <ArrowRight className="w-4 h-4" />
            </Link>
          </>
        )}
        {status === 'error' && (
          <>
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <p className="text-zinc-300 mb-6">{error}</p>
            <Link to="/login" className="text-amber-400 hover:underline">Aller à la page de connexion</Link>
          </>
        )}
      </div>
    </PageShell>
  );
};

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      toast.error('Erreur, réessaye dans un instant');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell title="Mot de passe oublié">
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-8">
        {sent ? (
          <div className="text-center">
            <Check className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <p className="text-zinc-300 mb-2 font-semibold">Email envoyé</p>
            <p className="text-zinc-400 text-sm">Si un compte existe avec cet email, tu recevras un lien pour réinitialiser ton mot de passe (valide 1h).</p>
            <Link to="/login" className="block mt-6 text-amber-400 hover:underline text-sm">← Retour à la connexion</Link>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            <p className="text-sm text-zinc-400 mb-4">Saisis ton email, on t'envoie un lien de réinitialisation.</p>
            <div>
              <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wide mb-1.5">Email</label>
              <input
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ton@email.fr"
                className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-4 py-2.5 text-white placeholder-zinc-600 focus:outline-none focus:border-amber-500"
                data-testid="forgot-email"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-zinc-950 font-bold transition-colors"
              data-testid="forgot-submit"
            >
              {loading ? 'Envoi...' : 'Envoyer le lien'}
            </button>
            <Link to="/login" className="block text-center text-sm text-zinc-500 hover:text-amber-400">← Retour à la connexion</Link>
          </form>
        )}
      </div>
    </PageShell>
  );
};

export const ResetPasswordPage = () => {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) { toast.error('8 caractères minimum'); return; }
    if (pwd !== pwd2) { toast.error('Les mots de passe ne correspondent pas'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pwd });
      toast.success('Mot de passe réinitialisé !');
      setTimeout(() => navigate('/login'), 1000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  if (!token) return <PageShell title="Lien invalide"><p className="text-center text-zinc-400">Token manquant. <Link to="/login" className="text-amber-400 underline">Retour</Link></p></PageShell>;

  return (
    <PageShell title="Nouveau mot de passe">
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-8">
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wide mb-1.5">Nouveau mot de passe</label>
            <input required type="password" minLength={8} value={pwd} onChange={(e) => setPwd(e.target.value)}
              className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-amber-500"
              data-testid="reset-pwd"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wide mb-1.5">Confirmer</label>
            <input required type="password" minLength={8} value={pwd2} onChange={(e) => setPwd2(e.target.value)}
              className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-amber-500"
              data-testid="reset-pwd2"
            />
          </div>
          <button type="submit" disabled={loading} className="w-full py-3 rounded-lg bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-zinc-950 font-bold transition-colors" data-testid="reset-submit">
            {loading ? 'Mise à jour...' : 'Changer mon mot de passe'}
          </button>
        </form>
      </div>
    </PageShell>
  );
};

export const BillingSuccessPage = () => {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState('checking');

  useEffect(() => {
    if (!sessionId) { setStatus('missing'); return; }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await axios.get(`${API}/billing/checkout-status/${sessionId}`, { withCredentials: true });
        if (res.data.payment_status === 'paid' || res.data.subscription_status === 'active') {
          setStatus('success');
          return;
        }
        if (attempts < 5) setTimeout(poll, 2000);
        else setStatus('pending');
      } catch (err) {
        if (attempts < 3) setTimeout(poll, 2000);
        else setStatus('error');
      }
    };
    poll();
  }, [sessionId]);

  return (
    <PageShell title={status === 'success' ? 'Bienvenue dans la team 🎉' : 'Vérification du paiement...'}>
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-8 text-center">
        {status === 'checking' && (<><Loader2 className="w-12 h-12 text-amber-400 animate-spin mx-auto mb-4" /><p className="text-zinc-400">On vérifie ton paiement avec Stripe...</p></>)}
        {status === 'success' && (
          <>
            <Check className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
            <p className="text-zinc-200 mb-2 font-semibold">Ton abonnement est actif !</p>
            <p className="text-zinc-400 text-sm mb-6">Tu reçois ta facture par email dans quelques minutes.</p>
            <Link to="/app" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold transition-colors" data-testid="billing-success-cta">
              Retourner au dashboard <ArrowRight className="w-4 h-4" />
            </Link>
          </>
        )}
        {status === 'pending' && (<><AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-4" /><p className="text-zinc-300">Paiement en cours de traitement... Tu recevras un email de confirmation. Tu peux <Link to="/" className="text-amber-400 underline">retourner au dashboard</Link>.</p></>)}
        {status === 'error' && (<><AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" /><p className="text-zinc-300">Impossible de vérifier le paiement. Si tu as bien été débité, le statut sera mis à jour automatiquement. <Link to="/" className="text-amber-400 underline">Dashboard</Link></p></>)}
        {status === 'missing' && (<p className="text-zinc-400">Paramètre manquant. <Link to="/" className="text-amber-400 underline">Retour</Link></p>)}
      </div>
    </PageShell>
  );
};

export const BillingCancelPage = () => (
  <PageShell title="Paiement annulé">
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-8 text-center">
      <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
      <p className="text-zinc-300 mb-6">Tu as annulé le paiement. Aucun débit n'a eu lieu.</p>
      <div className="flex gap-3 justify-center">
        <Link to="/pricing" className="px-5 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold transition-colors">Reprendre l'abonnement</Link>
        <Link to="/app" className="px-5 py-2.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 font-medium transition-colors">Dashboard</Link>
      </div>
    </div>
  </PageShell>
);
